import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const STORAGE_KEY = 'lr-install-dismissed';
const DISMISS_DAYS = 30;
const SHOW_DELAY_MS = 3000;

function isDismissed(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const { timestamp } = JSON.parse(raw);
    const elapsed = Date.now() - timestamp;
    return elapsed < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function detectIOS(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !(window as unknown as { MSStream?: unknown }).MSStream
  );
}

// Module-level singleton: the browser fires `beforeinstallprompt` once per page
// load. Capturing at module scope (before React mounts) ensures every hook
// instance sees the cached event, even if it mounts after the event fired.
let cachedPrompt: BeforeInstallPromptEvent | null = null;
let appInstalled = false;
const subscribers = new Set<() => void>();

function notifySubscribers() {
  subscribers.forEach((fn) => fn());
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault();
    cachedPrompt = e as BeforeInstallPromptEvent;
    notifySubscribers();
  });

  window.addEventListener('appinstalled', () => {
    cachedPrompt = null;
    appInstalled = true;
    notifySubscribers();
  });
}

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(cachedPrompt);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS] = useState(() => detectIOS());
  const [isInstalled, setIsInstalled] = useState(() => isStandalone() || appInstalled);

  useEffect(() => {
    if (isStandalone() || appInstalled) {
      setIsInstalled(true);
      return;
    }

    const sync = () => {
      setDeferredPrompt(cachedPrompt);
      if (appInstalled || isStandalone()) {
        setIsInstalled(true);
        setShowInstallBanner(false);
      }
    };

    sync();
    subscribers.add(sync);
    return () => {
      subscribers.delete(sync);
    };
  }, []);

  // Show banner after delay, once a prompt is available (Android/desktop) or on iOS
  useEffect(() => {
    if (isStandalone() || isDismissed()) return;
    if (!deferredPrompt && !isIOS) return;
    const timer = setTimeout(() => setShowInstallBanner(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [deferredPrompt, isIOS]);

  const triggerInstall = useCallback(async () => {
    if (!cachedPrompt) return;
    const prompt = cachedPrompt;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    cachedPrompt = null;
    notifySubscribers();
    if (outcome === 'accepted') {
      setShowInstallBanner(false);
    }
  }, []);

  const dismissPrompt = useCallback(() => {
    setShowInstallBanner(false);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ timestamp: Date.now() }));
    } catch {
      // localStorage unavailable — banner just hides for this session
    }
  }, []);

  return {
    showInstallBanner,
    isIOS,
    isInstalled,
    canNativelyInstall: deferredPrompt !== null,
    triggerInstall,
    dismissPrompt,
  };
}
